import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Coupon, CouponFormData } from '../../services/api';
import ChoicesSelect from '../ChoicesSelect';
import useClickOutside from '../../hooks/useClickOutside';

interface CouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (couponData: CouponFormData) => void;
  coupon: Coupon | null;
}

const CouponModal: React.FC<CouponModalProps> = ({ isOpen, onClose, onSave, coupon }) => {
  const getInitialFormData = (): CouponFormData => ({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    minimum_amount: undefined,
    maximum_discount: undefined,
    usage_limit: undefined,
    is_active: true,
    starts_at: '',
    expires_at: '',
  });

  const [formData, setFormData] = useState<CouponFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Click outside handler
  const modalRef = useClickOutside(onClose);

  useEffect(() => {
    if (isOpen) {
      if (coupon) {
        setFormData({
          ...getInitialFormData(),
          ...coupon,
          starts_at: coupon.starts_at ? new Date(coupon.starts_at).toISOString().split('T')[0] : '',
          expires_at: coupon.expires_at ? new Date(coupon.expires_at).toISOString().split('T')[0] : '',
        });
      } else {
        setFormData(getInitialFormData());
      }
      setErrors({});
    }
  }, [coupon, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev: CouponFormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (parseFloat(value) || undefined) : value,
    }));

    if (errors[name]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string, value: string | number) => {
    setFormData((prev: CouponFormData) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev: Record<string, string>) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) newErrors.code = 'Coupon code is required';
    if (!formData.name.trim()) newErrors.name = 'Coupon name is required';
    if (formData.discount_value <= 0) newErrors.discount_value = 'Discount value must be greater than 0';
    if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
      newErrors.discount_value = 'Percentage cannot exceed 100';
    }
    if (formData.starts_at && formData.expires_at && formData.starts_at > formData.expires_at) {
      newErrors.expires_at = 'Expires date must be after the start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {coupon ? 'Edit Coupon' : 'Add New Coupon'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
            {/* Column 1 */}
            <div className="space-y-6">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">Coupon Code *</label>
                <input 
                  type="text" 
                  name="code" 
                  id="code" 
                  value={formData.code} 
                  onChange={handleInputChange} 
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${errors.code ? 'border-red-500' : ''}`} 
                  placeholder="Enter coupon code"
                />
                {errors.code && <p className="text-xs text-red-500 mt-1">{errors.code}</p>}
              </div>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Coupon Name *</label>
                <input 
                  type="text" 
                  name="name" 
                  id="name" 
                  value={formData.name} 
                  onChange={handleInputChange} 
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${errors.name ? 'border-red-500' : ''}`} 
                  placeholder="Enter coupon name"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea 
                  name="description" 
                  id="description" 
                  value={formData.description || ''} 
                  onChange={handleInputChange} 
                  rows={3} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]" 
                  placeholder="Enter coupon description"
                />
              </div>
              <div>
                <label htmlFor="discount_type" className="block text-sm font-medium text-gray-700 mb-2">Discount Type *</label>
                <ChoicesSelect
                  options={[
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'fixed_amount', label: 'Fixed Amount' },
                  ]}
                  value={formData.discount_type}
                  onChange={(value) => handleSelectChange('discount_type', value)}
                  className={`w-full ${errors.discount_type ? 'border-red-500' : ''}`}
                />
              </div>
              <div>
                <label htmlFor="discount_value" className="block text-sm font-medium text-gray-700 mb-2">Discount Value *</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    {formData.discount_type === 'percentage' ? <span className="text-gray-500 text-sm">%</span> : <span className="text-gray-500 text-sm">Ks</span>}
                  </div>
                  <input 
                    type="number" 
                    name="discount_value" 
                    id="discount_value" 
                    value={formData.discount_value} 
                    onChange={handleInputChange} 
                    className={`w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${errors.discount_value ? 'border-red-500' : ''}`} 
                    placeholder="0.00" 
                  />
                </div>
                {errors.discount_value && <p className="text-xs text-red-500 mt-1">{errors.discount_value}</p>}
              </div>
            </div>

            {/* Column 2 */}
            <div className="space-y-6">
              <div>
                <label htmlFor="minimum_amount" className="block text-sm font-medium text-gray-700 mb-2">Minimum Purchase (optional)</label>
                <input 
                  type="number" 
                  name="minimum_amount" 
                  id="minimum_amount" 
                  value={formData.minimum_amount || ''} 
                  onChange={handleInputChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]" 
                  placeholder="e.g., 50000" 
                />
              </div>
              {formData.discount_type === 'percentage' && (
                <div>
                  <label htmlFor="maximum_discount" className="block text-sm font-medium text-gray-700 mb-2">Maximum Discount (optional)</label>
                  <input 
                    type="number" 
                    name="maximum_discount" 
                    id="maximum_discount" 
                    value={formData.maximum_discount || ''} 
                    onChange={handleInputChange} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]" 
                    placeholder="e.g., 10000" 
                  />
                </div>
              )}
              <div>
                <label htmlFor="usage_limit" className="block text-sm font-medium text-gray-700 mb-2">Usage Limit (optional)</label>
                <input 
                  type="number" 
                  name="usage_limit" 
                  id="usage_limit" 
                  value={formData.usage_limit || ''} 
                  onChange={handleInputChange} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]" 
                  placeholder="e.g., 100" 
                />
              </div>
              <div>
                <label htmlFor="starts_at" className="block text-sm font-medium text-gray-700 mb-2">Start Date (optional)</label>
                <input 
                  type="date" 
                  name="starts_at" 
                  id="starts_at" 
                  value={formData.starts_at || ''} 
                  onChange={handleInputChange} 
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${errors.starts_at ? 'border-red-500' : ''}`} 
                />
                {errors.starts_at && <p className="text-xs text-red-500 mt-1">{errors.starts_at}</p>}
              </div>
              <div>
                <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700 mb-2">Expires Date (optional)</label>
                <input 
                  type="date" 
                  name="expires_at" 
                  id="expires_at" 
                  value={formData.expires_at || ''} 
                  onChange={handleInputChange} 
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998] ${errors.expires_at ? 'border-red-500' : ''}`} 
                />
                {errors.expires_at && <p className="text-xs text-red-500 mt-1">{errors.expires_at}</p>}
              </div>
              <div className="flex items-center">
                <input 
                  id="is_active" 
                  name="is_active" 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={handleInputChange} 
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" 
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">Active</label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {coupon ? 'Update Coupon' : 'Create Coupon'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CouponModal;
